/**
 * Two-Factor Authentication Hooks
 *
 * React Query hooks para 2FA
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as twoFactorService from './two-factor.service'

/**
 * Query keys
 */
export const twoFactorKeys = {
  all: ['twoFactor'] as const,
  status: () => [...twoFactorKeys.all, 'status'] as const,
}

/**
 * Hook para obtener estado de 2FA
 */
export function useTwoFactorStatus() {
  return useQuery({
    queryKey: twoFactorKeys.status(),
    queryFn: twoFactorService.get2FAStatus,
  })
}

/**
 * Hook para iniciar setup de 2FA
 */
export function useSetup2FA() {
  return useMutation({
    mutationFn: twoFactorService.setup2FA,
  })
}

/**
 * Hook para verificar código 2FA
 */
export function useVerify2FA() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (code: string) => twoFactorService.verify2FA(code),
    onSuccess: () => {
      // Invalidar estado de 2FA para refrescar
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() })
    },
  })
}

/**
 * Hook para desactivar 2FA
 */
export function useDisable2FA() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (code: string) => twoFactorService.disable2FA(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: twoFactorKeys.status() })
    },
  })
}

/**
 * Hook para validar 2FA durante login
 */
export function useValidate2FA() {
  return useMutation({
    mutationFn: ({ userId, code }: { userId: string; code: string }) =>
      twoFactorService.validate2FA(userId, code),
  })
}

/**
 * Hook para completar login después de verificar 2FA
 * Retorna los tokens de acceso
 */
export function useComplete2FALogin() {
  return useMutation({
    mutationFn: ({ userId, code }: { userId: string; code: string }) =>
      twoFactorService.complete2FALogin(userId, code),
  })
}
